import { app, ojs, payload, state } from "modular-openscriptjs";
import {
    Middleware,
    PAGES,
    analytics,
    api,
    uc,
    gc,
    dc,
    resolveCurrentPageUsing,
} from "./declarations";
import {
    fireEvent,
    setActiveNav,
} from "./helpers";
import App from "./components/App";

// Page Imports
import Index from "./components/Pages/Index";
import LoginPage from "./components/Pages/LoginPage";
import SignUp from "./components/Pages/Users/SignUp";
import Page404 from "./components/Pages/Page404";
import SingleProduct from "./components/Pages/Products/SingleProduct";
import SearchPage from "./components/Pages/SearchPage";
import Setting from "./components/Pages/Setting";
import BusinessesPage from "./components/Pages/Businesses/BusinessesPage";
import Register from "./components/Pages/Businesses/Register";
import AddProduct from "./components/Pages/Businesses/AddProduct";
import AddProductsFromImages from "./components/Pages/Businesses/AddProductsFromImages";
import EditProduct from "./components/Pages/Businesses/EditProduct";
import Orders from "./components/Pages/Businesses/Orders";
import SelectCompany from "./components/Pages/Businesses/SelectCompany";
import ManageBusiness from "./components/Pages/Businesses/ManageBusiness";
import ManageBranches from "./components/Pages/Businesses/ManageBranches";
import ManageBranch from "./components/Pages/Businesses/ManageBranch";
import ManagePrimaryInfo from "./components/Pages/Businesses/ManagePrimaryInfo";
import ManageSupportInfo from "./components/Pages/Businesses/ManageSupportInfo";
import ManageLegalInfo from "./components/Pages/Businesses/ManageLegalInfo";
import ManageStaff from "./components/Pages/Businesses/ManageStaff";
import ManageInventory from "./components/Pages/Businesses/ManageInventory";
import ManageServices from "./components/Pages/Businesses/ManageServices";
import ViewServices from "./components/Pages/Businesses/ViewServices";
import AddService from "./components/Pages/Businesses/AddService";
import EditService from "./components/Pages/Businesses/EditService";
import SubCategoriesPage from "./components/Pages/Search/SubCategoriesPage";
import BrowseCategoryPage from "./components/Pages/Search/BrowseCategoryPage";
import AcceptInvitation from "./components/Pages/Invitations/AcceptInvitation";
import BusinessAnalytics from "./components/Pages/Businesses/BusinessAnalytics";
import Reviews from "./components/Pages/Businesses/Reviews";
import OrderDetails from "./components/Pages/Businesses/OrderDetails";
import Notifications from "./components/Pages/Businesses/Notifications";
import DeliveryRequests from "./components/Pages/Businesses/DeliveryRequests";

import Profile from "./components/Pages/Users/Profile";
import EditProfile from "./components/Pages/Users/EditProfile";
import PasswordPage from "./components/Pages/Users/Password";
import ForgotPassword from "./components/Pages/Users/ForgotPassword";
import ResetPassword from "./components/Pages/Users/ResetPassword";
import OtpAuthPage from "./components/Pages/OtpAuth";
import AiCreditWallet from "./components/Pages/Users/AiCreditWallet";
import DepositAiCreditForm from "./components/Forms/DepositAiCreditForm";
import PaymentPage from "./components/Pages/PaymentPage";
import ConfirmMpesaPayment from "./components/Forms/ConfirmMpesaPayment";

const router = app("router");
const h = app("h");
const broker = app("broker");
const $e = app("$e");

ojs(
    App,
    Index,
    LoginPage,
    SignUp,
    Page404,
    SingleProduct,
    SearchPage,
    Setting,
    BusinessesPage,
    Register,
    AddProduct,
    AddProductsFromImages,
    EditProduct,
    Orders,
    SelectCompany,
    ManageBusiness,
    ManageBranches,
    ManageBranch,
    ManagePrimaryInfo,
    ManageSupportInfo,
    ManageLegalInfo,
    ManageStaff,
    ManageInventory,
    ManageServices,
    ViewServices,
    AddService,
    EditService,
    SubCategoriesPage,
    BrowseCategoryPage,
    AcceptInvitation,
    BusinessAnalytics,
    Reviews,
    OrderDetails,
    Notifications,
    DeliveryRequests,

    Profile,
    EditProfile,
    PasswordPage,
    ForgotPassword,
    ResetPassword,
    OtpAuthPage,
    AiCreditWallet,
    DepositAiCreditForm,
    PaymentPage,
    ConfirmMpesaPayment,
);

const renderInApp = (callback) => {
    // resolves the current page in the main content component directly
    resolveCurrentPageUsing(() => callback());

    return h.App(gc.appLoadedAt, {
        parent: gc.root,
        resetParent: router.reset,
        reconcileParent: true,
    });
};

const m = new Middleware();

router.default(() => router.to("page-not-found"));

router.on("/404", () => renderInApp(() => h.Page404()), "page-not-found");

router
    .orOn(
        ["/", "/index"],
        () => {
            setActiveNav('home');
            let f = () => {
                renderInApp(() => h.Index(gc.auth));
                analytics.pageView("Index");
            };

            if(gc.auth.value) {
                m.auth()?.company(f);
            } else {
                f();
            }
        },
        ["home", "index"],
    )
    .on(
        "/settings",
        () => {
            setActiveNav('settings');
            m.auth(() => {
                renderInApp(() => h.Setting());
                fireEvent($e.setting.needs.information);
                analytics.pageView(PAGES.SETTINGS);
            });
        },
        "user.settings",
    )
    .on(
        "/businesses",
        () => {
            setActiveNav('switch-business');
            renderInApp(() => h.BusinessesPage());
            analytics.pageView("businesses");
        },
        "all-businesses",
    )
    .on(
        "/businesses/register",
        () => {
            setActiveNav('business-info');
            m.auth(() => {
                renderInApp(() => h.Register());
                analytics.pageView("business.register");
            });
        },
        "business.register",
    )
    .on(
        "/businesses/{companyId}/products/add",
        () => {
            setActiveNav('inventory');
            m.auth()?.company()?.branches(() => {
                renderInApp(() => h.AddProduct());
                analytics.pageView("business.products.add");
            });
        },
        "business.products.add",
    )
    .on(
        "/businesses/{companyId}/inventory/add-from-images",
        () => {
            setActiveNav('inventory');
            m.auth()?.company()?.branches(() => {
                renderInApp(() => h.AddProductsFromImages());
                analytics.pageView("business.products.add_images");
            });
        },
        "business.products.add_images",
    )
    .on(
        "/businesses/{companyId}/products/{productId}/edit",
        () => {
            setActiveNav('inventory');
            m.auth()?.company()?.branches(() => {
                renderInApp(() => h.EditProduct());
                analytics.pageView("business.products.edit");
            });
        },
        "business.products.edit",
    )
    .on(
        "/businesses/{companyId}/inventory",
        () => {
            setActiveNav('inventory');
            m.auth()?.company()?.branches(() => {
                renderInApp(() => h.ManageInventory());
                analytics.pageView("business.manage.inventory");
            });
        },
        "business.manage.inventory",
    )
    .on(
        "/businesses/{companyId}/services/manage",
        () => {
            setActiveNav('services');
            m.auth()?.company()?.branches(() => {
                renderInApp(() => h.ManageServices());
                analytics.pageView("business.services.manage");
            });
        },
        "business.services.manage",
    )
    .on(
        "/businesses/{companyId}/services/view",
        () => {
            setActiveNav('services');
            m.auth()?.company()?.branches(() => {
                renderInApp(() => h.ViewServices());
                analytics.pageView("business.services.view");
            });
        },
        "business.services.view",
    )
    .on(
        "/businesses/{companyId}/services/add",
        () => {
            setActiveNav('services');
            m.auth()?.company()?.branches(() => {
                renderInApp(() => h.AddService());
                analytics.pageView("business.services.add");
            });
        },
        "business.services.add",
    )
    .on(
        "/businesses/{companyId}/services/{serviceId}/edit",
        () => {
            setActiveNav('services');
            m.auth()?.company()?.branches(() => {
                renderInApp(() => h.EditService());
                analytics.pageView("business.services.edit");
            });
        },
        "business.services.edit",
    )
    .on(
        "/businesses/{companyId}/inventory/category/{categoryId}/subcategories",
        () => {
            setActiveNav('inventory');
            m.auth()?.company()?.branches(() => {
                renderInApp(() => h.SubCategoriesPage());
                analytics.pageView("business.inventory.category.subcategories");
            });
        },
        "business.inventory.category.subcategories",
    )
    .on(
        "/businesses/{companyId}/inventory/category/{categoryId}/browse",
        () => {
            setActiveNav('inventory');
            m.auth()?.company()?.branches(() => {
                renderInApp(() => h.BrowseCategoryPage());
                analytics.pageView("business.inventory.category.browse");
            });
        },
        "business.inventory.category.browse",
    )
    .on(
        "/businesses/{companyId}/inventory/subcategory/{subcategoryId}/browse",
        () => {
            setActiveNav('inventory');
            m.auth()?.company()?.branches(() => {
                renderInApp(() => h.BrowseCategoryPage());
                analytics.pageView("business.inventory.subcategory.browse");
            });
        },
        "business.inventory.subcategory.browse",
    )
    .on(
        "/businesses/{companyId}/inventory/view",
        () => {
            setActiveNav('inventory');
            m.auth()?.company()?.branches(() => {
                renderInApp(() => h.ManageInventory());
                analytics.pageView("business.inventory.view");
            });
        },
        "business.inventory.view",
    )
    .on(
        "/businesses/{companyId}/orders",
        () => {
            setActiveNav('orders');
            m.auth()?.company()?.branches(() => {
                renderInApp(() => h.Orders(state(router.params.companyId)));
                analytics.pageView("business.orders");
            });
        },
        "business.orders",
    )
    .on(
        "/businesses/{companyId}/delivery-requests",
        () => {
            setActiveNav('delivery-requests');
            m.auth()?.company()?.branches(() => {
                renderInApp(() => h.DeliveryRequests(state(router.params.companyId)));
                analytics.pageView("business.delivery-requests");
            });
        },
        "business.delivery-requests",
    )

    .on(
        "/businesses/orders/{orderId}/details",
        () => {
            setActiveNav('orders');
            m.auth()?.company()?.branches(() => {
                renderInApp(() => h.OrderDetails());
                analytics.pageView("business.order-details");
            });
        },
        "business.order-details",
    )
    .on(
        "/businesses/select",
        () => {
            setActiveNav('switch-business');
            m.auth(() => {
                renderInApp(() => h.SelectCompany());
                analytics.pageView("business.select");
            });
        },
        "business.select",
    )
    .on(
        "/businesses/manage",
        () => {
            setActiveNav('business-info');
            m.auth()?.company(() => {
                renderInApp(() => h.ManageBusiness());
                analytics.pageView("business.manage");
            });
        },
        "business.manage",
    )
    .on(
        "/businesses/{companyId}/manage/branches",
        () => {
            setActiveNav('business-info');
            m.auth()?.company(() => {
                renderInApp(() => h.ManageBranches());
                analytics.pageView("business.manage.branches");
            });
        },
        "business.manage.branches",
    )
    .on(
        "/businesses/{companyId}/branches/{branchId}/manage",
        () => {
            setActiveNav('business-info');
            m.auth()?.company()?.branches(() => {
                renderInApp(() => h.ManageBranch());
                analytics.pageView("business.branch.manage");
            });
        },
        "business.branch.manage",
    )
    .on(
        "/businesses/{companyId}/manage/primary",
        () => {
            setActiveNav('business-info');
            m.auth()?.company(() => {
                renderInApp(() => h.ManagePrimaryInfo());
                analytics.pageView("business.manage.primary");
            });
        },
        "business.manage.primary",
    )
    .on(
        "/businesses/{companyId}/manage/support",
        () => {
            setActiveNav('business-info');
            m.auth()?.company(() => {
                renderInApp(() => h.ManageSupportInfo());
                analytics.pageView("business.manage.support");
            });
        },
        "business.manage.support",
    )
    .on(
        "/businesses/{companyId}/manage/legal",
        () => {
            setActiveNav('business-info');
            m.auth()?.company(() => {
                renderInApp(() => h.ManageLegalInfo());
                analytics.pageView("business.manage.legal");
            });
        },
        "business.manage.legal",
    )
    .on(
        "/businesses/{companyId}/manage/staff",
        () => {
            setActiveNav('business-info');
            m.auth()?.company(() => {
                renderInApp(() => h.ManageStaff());
                analytics.pageView("business.manage.staff");
            });
        },
        "business.manage.staff",
    )
    .on(
        "/analytics",
        () => {
            setActiveNav('analytics');
            m.auth()?.company()?.branches(() => {
                renderInApp(() => h.BusinessAnalytics());
                analytics.pageView("business.analytics");
            });
        },
        "business.analytics",
    )
    .on("/orders", () => {
        setActiveNav('orders');
        m.auth()?.company()?.branches(() => {
            return router.to("business.orders", {
                companyId: window.currentCompany.id,
            });
        });
    })
    .on("/manage", () => {
        setActiveNav('business-info');
        m.auth()?.company(() => {
            return router.to("business.manage", {
                companyId: window.currentCompany.id,
            });
        });
    })
    .on("/current-services", () => {
        setActiveNav('services');
        if (window.currentCompany) {
            return router.to("business.services.manage", {
                companyId: window.currentCompany.id,
            });
        }
    })
    .on("/current-delivery-requests", () => {
        setActiveNav('delivery-requests');
        if (window.currentCompany) {
            return router.to("business.delivery-requests", {
                companyId: window.currentCompany.id,
            });
        }
    })

    .on("/current-inventory", () => {
        setActiveNav('inventory');
        m.auth()?.company()?.branches(() => {
            return router.to("business.manage.inventory", {
                companyId: window.currentCompany.id,
            });
        });
    })
    .on(
        "/businesses/{companyId}/reviews",
        () => {
            setActiveNav('business-info');
            m.auth()?.company()?.branches(() => {
                renderInApp(() => h.Reviews());
                analytics.pageView("business.reviews");
            });
        },
        "business.reviews",
    )
    .on(
        "/businesses/{companyId}/notifications",
        () => {
            setActiveNav('profile');
            m.auth()?.company()?.branches(() => {
                renderInApp(() => h.Notifications());
                analytics.pageView("business.notifications");
            });
        },
        "business.notifications",
    )
    .on(
        "/ai-credits",
        () => {
            setActiveNav('ai-credits');
            m.auth(() => {
                renderInApp(() => h.AiCreditWallet());
                fireEvent($e.aiCredit.needs.balance);
                analytics.pageView(PAGES.AI_CREDITS);
            });
        },
        "user.ai-credits",
    )
    .on(
        "/wallet",
        () => {
            setActiveNav('ai-credits');
            m.auth(() => {
                renderInApp(() => h.AiCreditWallet());
                fireEvent($e.aiCredit.needs.balance);
                analytics.pageView(PAGES.AI_CREDITS);
            });
        },
        "user.wallet",
    );

router.on(
    "/login",
    () => {
        setActiveNav('login');
        renderInApp(() => h.LoginPage());
        analytics.pageView("Login");
    },
    "user.login",
);

router.prefix("/products").group(() => {
    router.on(
        "/{productId}/view",
        () => {
            setActiveNav('inventory');
            renderInApp(() => h.SingleProduct());
            analytics.pageView("Single Product");
        },
        "product.view-product",
    );
});

router.prefix("users").group(() => {
    router.on(
        "/profile",
        () => {
            setActiveNav('profile');
            m.auth(() => {
                renderInApp(() => h.Profile(gc.user));
                analytics.pageView("View Profile");
            });
        },
        "user.view-profile",
    );

    router.on(
        "/profile/edit",
        () => {
            setActiveNav('profile');
            m.auth(() => {
                renderInApp(() => h.EditProfile(gc.user));
                analytics.pageView("Edit Profile");
            });
        },
        "user.edit-profile",
    );

    router.on(
        "/profile/change-password",
        () => {
            setActiveNav('profile');
            m.auth(() => {
                renderInApp(() => h.PasswordPage());
                analytics.pageView("Change Password");
            });
        },
        "user.change-password",
    );

    router.on(
        "/sign-up",
        () => {
            setActiveNav('sign-up');
            renderInApp(() => h.SignUp());

            analytics.pageView("Sign Up");
        },
        "user.sign-up",
    );

    router.on(
        "/forgot-password",
        () => {
            setActiveNav('login');
            renderInApp(() => h.ForgotPassword());
            analytics.pageView("Forgot Password");
        },
        "user.forgot-password",
    );

    router.on(
        "/reset-password",
        () => {
            setActiveNav('login');
            renderInApp(() => h.ResetPassword());
            analytics.pageView("Reset Password");
        },
        "user.reset-password",
    );

    router.on(
        "/otp",
        () => {
            setActiveNav('profile');
            m.auth(() => {
                renderInApp(() => h.OtpAuthPage());
                analytics.pageView("OtpAuth");
            });
        },
        "user.verify-identity",
    );
});

router.on(
    "/search",
    () => {
        setActiveNav('search');
        m.auth().company(() => {
            renderInApp(() => h.SearchPage());
            analytics.pageView("Search Page");
        });
    },
    "app.search",
);

router.on(
    "/invitations/accept",
    () => {
        setActiveNav('profile');
        m.auth(() => {
            renderInApp(() => h.AcceptInvitation());
            analytics.pageView("invitation.accept");
        });
    },
    "invitation.accept",
);

router.orOn(
    ["payments/pay/{amount}/{currency}", "payment/collect/{amount}/{currency}"],
    () => {
        setActiveNav('ai-credits');
        m.auth(() => {
            renderInApp(() => h.PaymentPage());
            analytics.pageView("Collect Payment");
        });
    },
    ["payments.collect"],
);

router.prefix("payment").group(() => {
    router.on("mpesa/process", () => {
        setActiveNav('ai-credits');
        m.auth(() => {
            renderInApp(() => h.ConfirmMpesaPayment());
            analytics.pageView("Mpesa Payment Confirmation");
        });
    });
});
